import { Tooltip } from "@fluentui/react-components";
import {
  Add16Regular,
  BookOpen16Regular,
  CalendarLtr16Regular,
  CheckmarkCircle16Regular,
  ChevronDown16Regular,
  Dismiss16Regular,
  Document16Regular,
  History16Regular,
  Link16Regular,
  QuestionCircle12Regular,
  TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppDialog, DialogCheckbox } from "@/components/app-dialog";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/date-field";
import { DetailSection } from "@/components/detail-section";
import { FuiButton, FuiDropdown, FuiInput, type FuiOption } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { Textarea } from "@/components/ui/textarea";
import { todayISO } from "@/engine/date";
import type { Dependency, DependencyType, ProgressMethod, ScheduleDriver, Task } from "@/engine/types";
import { LinkedDocumentRows } from "@/features/docs/LinkedDocuments";
import { LinkedTasksDialog } from "@/features/tasks/LinkedTasksDialog";
import { AssigneePicker } from "@/features/tasks/pickers";
import { knownAssignees } from "@/store/execution-task-ops";
import { tasksLinkedTo } from "@/features/tasks/task-query";
import { cn } from "@/lib/utils";
import { formatAgo, formatDate, formatDayHeading } from "@/lib/date-format";
import { displayBaselineName } from "@/lib/format";
import { useCan, useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { useCatalogStore } from "@/store/catalog-store";
import type { ChangeLogEntry } from "@/store/project-store";
import { useProjectStore } from "@/store/project-store";

export { DetailSection as Section } from "@/components/detail-section";

const PROGRESS_OPTIONS = [
  { value: "percent", label: "퍼센트 완료 (주관 추정)" },
  { value: "0/100", label: "0/100 (끝나야 인정)" },
  { value: "50/50", label: "50/50 (착수 50, 완료 100)" },
  { value: "milestone", label: "가중 마일스톤" },
];

const PROGRESS_METHOD_GUIDE: {
  method: ProgressMethod;
  title: string;
  detail: string;
}[] = [
  {
    method: "percent",
    title: "퍼센트 완료",
    detail: "입력한 %를 그대로 진척으로 씁니다. 중간 진행을 직접 적을 때 씁니다.",
  },
  {
    method: "0/100",
    title: "0/100",
    detail: "100%가 되기 전에는 0%로 계산합니다. 끝나야 진척으로 인정합니다.",
  },
  {
    method: "50/50",
    title: "50/50",
    detail: "아직 시작하지 않으면 0%, 시작하면 50%, 끝나면 100%로 계산합니다.",
  },
  {
    method: "milestone",
    title: "가중 마일스톤",
    detail: "퍼센트 완료와 같이, 입력한 %를 그대로 진척으로 씁니다.",
  },
];

const CONSTRAINT_OPTIONS = [
  { value: "ASAP", label: "가능한 빨리" },
  { value: "SNET", label: "이후 시작 (SNET)" },
  { value: "MSO", label: "고정 시작 (MSO)" },
];

const DEP_TYPE_GUIDE: {
  type: DependencyType;
  korean: string;
  english: string;
  menuHint: string;
  detail: string;
}[] = [
  {
    type: "FS",
    korean: "종료 후 시작",
    english: "Finish-to-Start",
    menuHint: "앞 작업이 끝나야 이 작업 시작",
    detail: "앞 작업 종료일 이후에 이 작업을 시작합니다. 일정에서 가장 많이 씁니다.",
  },
  {
    type: "SS",
    korean: "시작과 함께 시작",
    english: "Start-to-Start",
    menuHint: "앞 작업과 같은 날(또는 lag만큼) 시작",
    detail: "앞 작업 시작일을 기준으로 이 작업 시작일이 정해집니다. 병행 작업에 씁니다.",
  },
  {
    type: "FF",
    korean: "종료와 함께 종료",
    english: "Finish-to-Finish",
    menuHint: "앞 작업과 같은 날(또는 lag만큼) 종료",
    detail: "앞 작업 종료일을 기준으로 이 작업 종료일이 정해집니다.",
  },
  {
    type: "SF",
    korean: "시작 후 종료",
    english: "Start-to-Finish",
    menuHint: "앞 작업 시작을 기준으로 이 작업 종료",
    detail: "앞 작업 시작일을 기준으로 이 작업 종료일이 정해집니다. 드물게 씁니다.",
  },
];

const DEP_TYPE_OPTIONS: FuiOption[] = DEP_TYPE_GUIDE.map((g) => ({
  value: g.type,
  label: g.type,
  description: `${g.korean} · ${g.menuHint}`,
}));

const DEP_REL_KOREAN: Record<DependencyType, string> = Object.fromEntries(DEP_TYPE_GUIDE.map((g) => [g.type, g.korean])) as Record<
  DependencyType,
  string
>;

const SESSION_LOG_INLINE = 3;
const PCT_PRESETS = [0, 25, 50, 75, 100] as const;

const DEFAULT_SECTION_OPEN: Record<string, boolean> = {
  info: true,
  schedule: true,
  deps: true,
  tasks: true,
  wbsDict: false,
  docs: false,
  history: false,
};

export function Inspector({ taskId, onViewTasks }: { taskId?: string; onViewTasks?: (scheduleTaskId: string) => void }) {
  const storeFocusId = useProjectStore((s) => s.focusId);
  const focusId = taskId ?? storeFocusId;
  const task = useProjectStore((s) => (focusId ? s.project.tasks.find((t) => t.id === focusId) : undefined));
  const sched = useProjectStore((s) => (focusId ? s.derived.schedule.tasks.get(focusId) : undefined));
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(DEFAULT_SECTION_OPEN);

  const toggleSection = (key: string, open: boolean) => {
    setOpenSections((prev) => ({ ...prev, [key]: open }));
  };

  if (!task || !sched || !focusId) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <GhostEmptyState
          title="선택된 작업이 없습니다"
          description="WBS에서 작업을 클릭하면 일정, 선행 관계, 제약 조건을 확인할 수 있습니다."
        />
        <Button
          variant="outline"
          size="sm"
          className="mt-3 h-7 text-caption"
          onClick={() => useProjectStore.getState().setUI({ sidePanel: "insights" })}
        >
          플랜 인사이트 보기
        </Button>
      </div>
    );
  }
  return (
    <TaskInspector
      key={focusId}
      task={task}
      schedId={focusId}
      onViewTasks={onViewTasks}
      openSections={openSections}
      onToggleSection={toggleSection}
    />
  );
}

function TaskInspector({
  task,
  schedId,
  onViewTasks,
  openSections,
  onToggleSection,
}: {
  task: Task;
  schedId: string;
  onViewTasks?: (scheduleTaskId: string) => void;
  openSections: Record<string, boolean>;
  onToggleSection: (key: string, open: boolean) => void;
}) {
  const surface = usePlanSurface();
  const canEditDocs = useCan("plan.doc.edit");
  const own = useOwnWork();
  const canProgress = !!task && surface.editProgress && own.scheduleOwn(task);
  const sched = useProjectStore((s) => s.derived.schedule.tasks.get(schedId))!;
  const project = useProjectStore((s) => s.project);
  const baseline = useProjectStore((s) => s.derived.baseline);
  const changeLog = useProjectStore((s) => s.changeLog);
  const update = (patch: Partial<Task>, label?: string) => useProjectStore.getState().updateTask(task.id, patch, label);
  const resources = useCatalogStore((s) => s.resources);
  const assigneeSuggestions = useMemo(() => {
    const fromAssignments = (project.resourceAssignments || []).map((a) => resources[a.resourceId]?.displayName).filter((n): n is string => Boolean(n));
    return Array.from(new Set([...fromAssignments, ...knownAssignees(project)]));
  }, [project, resources]);
  const resourceIdByName = (name?: string) => {
    if (!name) return undefined;
    const assigned = (project.resourceAssignments || []).find((a) => resources[a.resourceId]?.displayName === name);
    if (assigned) return assigned.resourceId;
    return Object.values(resources).find((r) => r.isActive !== false && r.displayName === name)?.id;
  };
  const preds = project.dependencies.filter((d) => d.successorId === task.id);
  const succs = project.dependencies.filter((d) => d.predecessorId === task.id);
  const nameOf = (id: string) => {
    const t = project.tasks.find((x) => x.id === id);
    const s = useProjectStore.getState().derived.schedule.tasks.get(id);
    return t ? `${s?.wbs ?? ""} ${t.name || "이름 없음"}`.trim() : "?";
  };
  const bl = baseline?.tasks[task.id];
  const log = changeLog.filter((c) => c.taskIds.includes(task.id));
  const linkedDocCount = project.documentLinks.filter(
    (l) => l.targetKind === "scheduleTask" && l.targetId === task.id,
  ).length;

  return (
    <div className="thin-scrollbar h-full overflow-auto p-3 space-y-3">
      {/* Task Identity Hero */}
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono text-caption font-semibold px-2 py-0.5 rounded bg-muted text-foreground tabular">
            {sched.wbs}
          </span>
          <span className="text-caption px-2 py-0.5 rounded bg-muted text-muted-foreground">
            {sched.isSummary ? "요약 작업" : sched.isMilestone ? "마일스톤" : "작업"}
          </span>
          {sched.critical && (
            <span className="text-caption px-2 py-0.5 rounded bg-critical/15 text-critical font-medium">
              Critical
            </span>
          )}
          {sched.deadlineMissed && (
            <span className="text-caption px-2 py-0.5 rounded bg-critical/15 text-critical font-medium">
              마감 초과
            </span>
          )}
        </div>
        <div className="mt-2 text-title font-medium text-foreground break-words leading-snug">
          {task.name || "이름 없음"}
        </div>
      </div>

      {/* 1. 기본 정보 */}
      <DetailSection
        title="기본 정보"
        icon={<CheckmarkCircle16Regular className="size-4 text-tint" />}
        collapsible
        open={openSections.info ?? true}
        onToggle={(open) => onToggleSection("info", open)}
        variant="card"
      >
        <Field label="이름">
          {surface.editWbs ? (
            <DebouncedInput value={task.name} onCommit={(v) => v !== task.name && update({ name: v }, `이름 변경: ${v}`)} />
          ) : (
            <ReadValue value={task.name || "—"} />
          )}
        </Field>
        {!sched.isSummary && (
          <>
            <Field label="기간 (근무일)" hint="0이면 마일스톤">
              {surface.editSchedule ? (
                <DebouncedInput
                  type="number"
                  value={String(task.duration)}
                  onCommit={(v) => {
                    const n = Math.max(0, Math.round(Number(v)));
                    if (Number.isFinite(n) && n !== task.duration) update({ duration: n }, `${task.name}: 기간 ${task.duration}d → ${n}d`);
                  }}
                />
              ) : (
                <ReadValue value={`${task.duration}d`} />
              )}
            </Field>
            <Field label="진척률">
              {canProgress ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="inline-flex rounded-md border border-border bg-muted/60 p-0.5">
                      {PCT_PRESETS.map((p) => {
                        const active = task.percentComplete === p;
                        return (
                          <button
                            key={p}
                            type="button"
                            aria-pressed={active}
                            aria-label={`${p}%`}
                            onClick={() => update({ percentComplete: p }, `${task.name}: 진척 ${p}%`)}
                            className={cn(
                              "px-2 py-0.5 text-caption font-normal rounded transition-colors tabular",
                              active
                                ? "bg-card text-foreground font-medium shadow-none border border-border/80"
                                : "text-muted-foreground hover:text-foreground",
                            )}
                          >
                            {p}%
                          </button>
                        );
                      })}
                    </div>
                    <span className="text-body font-normal tabular text-foreground">{task.percentComplete}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={task.percentComplete}
                      aria-label="진척률 조절"
                      onChange={(e) => update({ percentComplete: Number(e.target.value) }, `${task.name}: 진척 ${e.target.value}%`)}
                      className="h-1 flex-1 accent-[var(--tint)]"
                    />
                  </div>
                </div>
              ) : (
                <ReadValue value={`${task.percentComplete}%`} />
              )}
            </Field>
            <Field label="측정 방법">
              {canProgress ? (
                <FuiDropdown
                  value={task.progressMethod}
                  options={PROGRESS_OPTIONS}
                  onValueChange={(v) => update({ progressMethod: v as ProgressMethod }, `${task.name}: 측정 방법 ${v}`)}
                />
              ) : (
                <ReadValue value={PROGRESS_OPTIONS.find((o) => o.value === task.progressMethod)?.label ?? task.progressMethod} />
              )}
              {sched.percentComplete !== task.percentComplete && (
                <p className="mt-1 text-caption text-muted-foreground">
                  입력 {task.percentComplete}% → 측정 방법 적용 {sched.percentComplete}%
                </p>
              )}
              <ProgressMethodGuide />
            </Field>
          </>
        )}
        <Field label="담당 인력">
          {surface.assignResource ? (
            <AssigneePicker
              value={task.assignee ? [task.assignee] : []}
              suggestions={assigneeSuggestions}
              onChange={(next) => {
                const name = next[next.length - 1];
                if (name === task.assignee) return;
                update({ assignee: name, assignedResourceId: resourceIdByName(name) }, `${task.name}: 담당 ${name ?? "해제"}`);
              }}
            />
          ) : (
            <ReadValue value={task.assignee || "—"} />
          )}
        </Field>
        {!sched.isSummary && (
          <Field label="계획 M/M (공수)" hint={`기준 21d 환산 시 약 ${(sched.duration / 21).toFixed(1)} M/M`}>
            {surface.editSchedule ? (
              <DebouncedInput
                type="number"
                step="0.1"
                value={task.plannedMM !== undefined ? String(task.plannedMM) : ""}
                placeholder={`${(sched.duration / 21).toFixed(1)} M/M`}
                onCommit={(v) => {
                  const n = v.trim() ? Math.round(Number(v) * 10) / 10 : undefined;
                  if (n !== task.plannedMM) update({ plannedMM: n }, `${task.name}: M/M ${n ?? "자동"}`);
                }}
              />
            ) : (
              <ReadValue value={task.plannedMM !== undefined ? String(task.plannedMM) : "—"} />
            )}
          </Field>
        )}
      </DetailSection>

      {/* 2. 일정 및 여유 2x2 그리드 */}
      <DetailSection
        title="일정"
        icon={<CalendarLtr16Regular className="size-4 text-muted-foreground" />}
        hint={sched.duration > 0 ? `${sched.duration}d` : undefined}
        collapsible
        open={openSections.schedule ?? true}
        onToggle={(open) => onToggleSection("schedule", open)}
        variant="card"
      >
        <div className="grid grid-cols-2 gap-2 mb-3">
          {/* 시작일 */}
          <div className="rounded-md border border-border/80 bg-muted/40 p-2.5">
            <div className="text-caption text-muted-foreground">시작일</div>
            <div className="mt-1 text-body font-normal text-foreground tabular">
              {formatDate(sched.start, "short")}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {formatDate(sched.start, "weekday")}
            </div>
          </div>

          {/* 종료일 */}
          <div className={cn("rounded-md border border-border/80 bg-muted/40 p-2.5", sched.deadlineMissed && "border-critical/40 bg-critical/5")}>
            <div className="text-caption text-muted-foreground">종료일</div>
            <div className={cn("mt-1 text-body font-normal tabular", sched.deadlineMissed ? "text-critical font-medium" : "text-foreground")}>
              {formatDate(sched.finish, "short")}
            </div>
            <div className={cn("text-[11px]", sched.deadlineMissed ? "text-critical" : "text-muted-foreground")}>
              {formatDate(sched.finish, "weekday")}
            </div>
          </div>

          {!sched.isSummary && (
            <>
              {/* 총 여유 */}
              <div className={cn("rounded-md border border-border/80 bg-muted/40 p-2.5", sched.totalFloat <= 0 && "border-critical/30 bg-critical/5")}>
                <div className="flex items-center justify-between text-caption text-muted-foreground">
                  <span>총 여유</span>
                  {sched.critical && <span className="text-[10px] text-critical font-medium">Critical</span>}
                </div>
                <div className={cn("mt-1 text-title font-normal tabular", sched.totalFloat <= 0 ? "text-critical font-medium" : "text-foreground")}>
                  {sched.totalFloat}d
                </div>
              </div>

              {/* 다음 여유 */}
              <div className="rounded-md border border-border/80 bg-muted/40 p-2.5">
                <div className="text-caption text-muted-foreground">다음 여유</div>
                <div className="mt-1 text-title font-normal text-foreground tabular">
                  {sched.freeFloat}d
                </div>
              </div>
            </>
          )}
        </div>

        <DriverNote driver={sched.driver} nameOf={nameOf} projectStart={project.start} autoConstraint={!!task.constraint?.auto} />

        {bl && (
          <div className="mb-2.5 rounded-md border border-border/70 bg-muted/40 p-2.5 text-body">
            <div className="mb-1.5 font-normal text-foreground">{displayBaselineName(baseline!.name)} 대비</div>
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">시작</span>
              <span className="font-normal tabular text-foreground">
                {formatDate(bl.start, "short")} → {formatDate(sched.start, "short")}
              </span>
            </div>
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">종료</span>
              <span className={cn("font-normal tabular", sched.finish > bl.finish ? "text-critical font-normal" : "text-foreground")}>
                {formatDate(bl.finish, "short")} → {formatDate(sched.finish, "short")}
              </span>
            </div>
          </div>
        )}

        {!sched.isSummary && (
          <>
            <Field label="시작 제약">
              {surface.editSchedule ? (
                <div className={task.constraint ? "flex flex-col gap-1.5" : undefined}>
                  <FuiDropdown
                    className="min-w-0 w-full"
                    value={task.constraint?.type ?? "ASAP"}
                    options={CONSTRAINT_OPTIONS}
                    onValueChange={(v) => {
                      if (v === "ASAP") update({ constraint: undefined }, `${task.name}: 제약 해제`);
                      else update({ constraint: { type: v as "SNET" | "MSO", date: task.constraint?.date ?? sched.start } }, `${task.name}: 제약 ${v}`);
                    }}
                  />
                  {task.constraint && (
                    <DateField
                      value={task.constraint.date}
                      onChange={(v) => v && update({ constraint: { type: task.constraint!.type, date: v } }, `${task.name}: 제약일 ${formatDate(v, "numeric")}`)}
                      className="w-full"
                      aria-label="제약일"
                    />
                  )}
                </div>
              ) : (
                <ReadValue
                  value={
                    task.constraint
                      ? `${CONSTRAINT_OPTIONS.find((o) => o.value === task.constraint?.type)?.label ?? task.constraint.type} · ${formatDate(task.constraint.date, "full")}`
                      : "가능한 빨리"
                  }
                />
              )}
              {task.constraint?.auto && (
                <p className="mt-1 text-caption text-muted-foreground font-normal">추가한 자리에 맞춘 임시 조건입니다. 앞 작업을 연결하면 자동으로 풀리고, 날짜를 직접 바꾸면 그대로 유지됩니다.</p>
              )}
              {sched.constraintConflict && <p className="mt-1 text-caption text-critical font-normal">선행 작업이 끝나기 전에 시작하도록 고정되어 있습니다.</p>}
            </Field>
            <Field label="마감 (Deadline)">
              {surface.editSchedule ? (
                <DateField
                  value={task.deadline}
                  defaultMonth={sched.finish}
                  clearable
                  placeholder="마감 없음"
                  onChange={(v) => update({ deadline: v }, `${task.name}: 마감 ${v ? formatDate(v, "numeric") : "해제"}`)}
                  aria-label="마감"
                />
              ) : (
                <ReadValue value={task.deadline ? formatDate(task.deadline, "full") : "—"} />
              )}
            </Field>
          </>
        )}
      </DetailSection>

      {/* 3. 선행 및 후행 관계 */}
      <DetailSection
        title="관계"
        icon={<Link16Regular className="size-4 text-muted-foreground" />}
        hint={`선행 ${preds.length}${succs.length > 0 ? ` · 후행 ${succs.length}` : ""}`}
        collapsible
        open={openSections.deps ?? true}
        onToggle={(open) => onToggleSection("deps", open)}
        variant="card"
      >
        <div className="mb-1.5 text-body font-normal text-muted-foreground">선행 {preds.length}</div>
        <ul className="space-y-1">
          {preds.map((d) => (
            <DepRow key={d.id} dep={d} otherId={d.predecessorId} nameOf={nameOf} editable={surface.editWbs} />
          ))}
        </ul>
        {surface.editWbs && <AddPredecessor taskId={task.id} />}
        {succs.length > 0 && (
          <>
            <div className="mb-1.5 mt-3 text-body font-normal text-muted-foreground">후행 {succs.length}</div>
            <ul className="space-y-1">
              {succs.map((d) => (
                <DepRow key={d.id} dep={d} otherId={d.successorId} nameOf={nameOf} editable={surface.editWbs} />
              ))}
            </ul>
          </>
        )}
        <DependencyTypeGuide />
      </DetailSection>

      {/* 4. 연계 Task */}
      <LinkedExecutionTasks
        scheduleTaskId={task.id}
        isSummary={sched.isSummary}
        start={sched.start}
        finish={sched.finish}
        onViewTasks={onViewTasks}
        canEdit={surface.editTask}
        canCreate={own.canCreateTask}
        isOpen={openSections.tasks ?? true}
        onToggle={(open) => onToggleSection("tasks", open)}
      />

      {/* 5. WBS Dictionary */}
      <DetailSection
        title="WBS Dictionary"
        icon={<BookOpen16Regular className="size-4 text-muted-foreground" />}
        hint={task.deliverable ? "산출물 1" : task.note ? "메모" : undefined}
        collapsible
        open={openSections.wbsDict ?? false}
        onToggle={(open) => onToggleSection("wbsDict", open)}
        variant="card"
      >
        <Field label="정의 / 메모">
          {surface.editWbs ? (
            <DebouncedTextarea value={task.note ?? ""} onCommit={(v) => v !== (task.note ?? "") && update({ note: v || undefined }, `${task.name}: 메모`)} placeholder="이 작업이 무엇을 하는지 한 문장으로" />
          ) : (
            <ReadValue value={task.note || "—"} />
          )}
        </Field>
        <Field label="산출물">
          {surface.editWbs ? (
            <DebouncedInput value={task.deliverable ?? ""} onCommit={(v) => v !== (task.deliverable ?? "") && update({ deliverable: v || undefined }, `${task.name}: 산출물`)} placeholder="예: 요구사항 정의서 v1.0" />
          ) : (
            <ReadValue value={task.deliverable || "—"} />
          )}
        </Field>
        <Field label="완료 기준">
          {surface.editWbs ? (
            <DebouncedInput value={task.acceptance ?? ""} onCommit={(v) => v !== (task.acceptance ?? "") && update({ acceptance: v || undefined }, `${task.name}: 완료 기준`)} placeholder="예: 현업 승인 서명" />
          ) : (
            <ReadValue value={task.acceptance || "—"} />
          )}
        </Field>
        {!sched.isSummary && (
          <Field label="가중치 (%)" hint="같은 단계 안의 작업을 더해 100%가 되어야 합니다">
            {surface.editWbs ? (
              <DebouncedInput
                type="number"
                value={task.weight === undefined ? "" : String(task.weight)}
                onCommit={(v) => {
                  const n = v === "" ? undefined : Number(v);
                  if (n !== task.weight) update({ weight: n }, `${task.name}: 가중치`);
                }}
                placeholder="비워두면 기간 가중"
              />
            ) : (
              <ReadValue value={task.weight === undefined ? "—" : String(task.weight)} />
            )}
          </Field>
        )}
      </DetailSection>

      {/* 6. 연관 문서 */}
      <DetailSection
        title="문서"
        icon={<Document16Regular className="size-4 text-muted-foreground" />}
        hint={linkedDocCount ? `${linkedDocCount}건` : undefined}
        collapsible
        open={openSections.docs ?? false}
        onToggle={(open) => onToggleSection("docs", open)}
        variant="card"
      >
        <LinkedDocumentRows kind="scheduleTask" targetId={task.id} canEdit={canEditDocs} />
      </DetailSection>

      {/* 7. 세션 변경 이력 */}
      <DetailSection
        title="변경 이력"
        icon={<History16Regular className="size-4 text-muted-foreground" />}
        hint={log.length ? `${log.length}건` : undefined}
        collapsible
        open={openSections.history ?? false}
        onToggle={(open) => onToggleSection("history", open)}
        variant="card"
      >
        <SessionChangeLog title={`${sched.wbs} ${task.name || "이름 없음"}`.trim()} entries={log} />
      </DetailSection>
    </div>
  );
}

function FinishDelta({ days }: { days: number }) {
  if (days === 0) return null;
  return (
    <span className={cn("shrink-0 font-normal tabular", days > 0 ? "text-critical" : "text-tint")}>
      종료일 {days > 0 ? "+" : ""}
      {days}d
    </span>
  );
}

/** Latest few entries inline; the full list lives in a dialog so the inspector never grows unbounded. */
function SessionChangeLog({ title, entries }: { title: string; entries: ChangeLogEntry[] }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (entries.length === 0) {
    return <p className="text-body text-muted-foreground">이번에 이 작업을 바꾼 기록이 없습니다.</p>;
  }

  return (
    <div>
      <ul className="space-y-1.5">
        {entries.slice(0, SESSION_LOG_INLINE).map((c) => (
          <li key={c.id} className="text-body">
            <div className="font-normal text-foreground">{c.label}</div>
            <div className="flex gap-2 text-caption text-muted-foreground">
              <time dateTime={c.at} title={formatDate(c.at, "dateTime")}>
                {formatAgo(c.at)}
              </time>
              <FinishDelta days={c.finishDeltaDays} />
            </div>
          </li>
        ))}
      </ul>
      {entries.length > SESSION_LOG_INLINE && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2 h-7 px-2 text-caption text-muted-foreground hover:text-foreground"
          onClick={() => setDialogOpen(true)}
        >
          전체 이력 보기 ({entries.length}건)
        </Button>
      )}
      <ChangeLogDialog open={dialogOpen} onClose={() => setDialogOpen(false)} title={title} entries={entries} />
    </div>
  );
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const localDayKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const dayHeading = (key: string) => formatDayHeading(key);

function ChangeLogDialog({ open, onClose, title, entries }: { open: boolean; onClose: () => void; title: string; entries: ChangeLogEntry[] }) {
  const [finishOnly, setFinishOnly] = useState(false);
  const shown = finishOnly ? entries.filter((c) => c.finishDeltaDays !== 0) : entries;

  const groups = useMemo(() => {
    const out: { key: string; items: ChangeLogEntry[] }[] = [];
    for (const c of shown) {
      const key = localDayKey(new Date(c.at));
      const last = out[out.length - 1];
      if (last?.key === key) last.items.push(c);
      else out.push({ key, items: [c] });
    }
    return out;
  }, [shown]);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="lg"
      title={`「${title}」 변경 이력`}
      footerStart={<span className="text-caption text-muted-foreground">이번 세션 동안만 보관됩니다. 새로고침하면 사라집니다.</span>}
      footer={
        <FuiButton appearance="secondary" density="dialog" onClick={onClose}>
          닫기
        </FuiButton>
      }
    >
      <div className="flex items-center justify-between gap-3">
        <DialogCheckbox label="종료일이 바뀐 변경만" checked={finishOnly} onChange={setFinishOnly} />
        <span className="shrink-0 text-caption text-muted-foreground tabular">
          {finishOnly ? `${shown.length} / ${entries.length}건` : `${entries.length}건`}
        </span>
      </div>
      <div className="mt-3 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
        {shown.length === 0 ? (
          <GhostEmptyState compact title="종료일이 바뀐 변경이 없습니다" description="이 작업의 변경은 모두 종료일에 영향을 주지 않았습니다." />
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-label={dayHeading(g.key)}>
              <h4 className="sticky top-0 border-b border-border bg-muted px-3 py-1 text-caption font-normal text-muted-foreground">{dayHeading(g.key)}</h4>
              <ul>
                {g.items.map((c) => (
                  <li key={c.id} className="flex items-baseline gap-3 border-b border-border/60 px-3 py-1.5 text-body last:border-b-0">
                    <time dateTime={c.at} title={formatAgo(c.at)} className="w-11 shrink-0 text-muted-foreground tabular">
                      {formatDate(c.at, "time")}
                    </time>
                    <span className="min-w-0 flex-1 text-foreground">{c.label}</span>
                    <FinishDelta days={c.finishDeltaDays} />
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </AppDialog>
  );
}

/** Planner execution tasks attached to this WBS node / activity (or anything beneath a summary). */
function LinkedExecutionTasks({
  scheduleTaskId,
  isSummary,
  start,
  finish,
  onViewTasks,
  canEdit,
  canCreate,
  isOpen = true,
  onToggle,
}: {
  scheduleTaskId: string;
  isSummary: boolean;
  start: string;
  finish: string;
  onViewTasks?: (scheduleTaskId: string) => void;
  canEdit: boolean;
  canCreate: boolean;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
}) {
  const executionTasks = useProjectStore((s) => s.project.executionTasks);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const own = useOwnWork();
  const s = useProjectStore.getState;
  const [dialogOpen, setDialogOpen] = useState(false);
  const linked = tasksLinkedTo(scheduleTaskId, executionTasks, schedule);
  const open = linked.filter((t) => t.status !== "done");
  const direct = new Set(executionTasks.filter((t) => t.linkedTaskIds.includes(scheduleTaskId)).map((t) => t.id));

  return (
    <>
      <DetailSection
        title="연계 Task"
        icon={<TaskListSquareLtr16Regular className="size-4 text-muted-foreground" />}
        hint={linked.length ? `${open.length}/${linked.length}` : undefined}
        collapsible
        open={isOpen}
        onToggle={onToggle}
        variant="card"
      >
        {linked.length === 0 ? (
          <p className="text-body text-muted-foreground">{isSummary ? "이 WBS 아래에 연결된 Task가 없습니다." : "이 작업에 연결된 Task가 없습니다."} 범위는 여기서, 오늘 할 일은 Task로.</p>
        ) : (
          <ul className="space-y-1">
            {linked.slice(0, 8).map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-1.5 py-1 text-left text-body hover:bg-muted"
                  onClick={() => s().setExecutionFocus(t.id)}
                  title={direct.has(t.id) ? "직접 연결" : "하위 작업에 연결"}
                >
                  {canEdit && own.executionOwn(t.assignees) ? (
                    <input
                      type="checkbox"
                      className="size-3.5 accent-[var(--tint)]"
                      checked={t.status === "done"}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => s().setExecutionStatus(t.id, e.target.checked ? "done" : "notStarted")}
                      aria-label="완료"
                    />
                  ) : (
                    <span className="w-8 shrink-0 text-caption text-muted-foreground">{t.status === "done" ? "완료" : "열림"}</span>
                  )}
                  <span className={cn("min-w-0 flex-1 truncate", t.status === "done" && "line-through text-muted-foreground")}>{t.title || "제목 없음"}</span>
                  {!direct.has(t.id) && <span className="text-muted-foreground">하위</span>}
                  {t.assignees[0] && <span className="shrink-0 text-muted-foreground">{t.assignees[0]}</span>}
                  {t.due && <span className={cn("shrink-0 tabular", t.status !== "done" && t.due < todayISO() ? "text-critical" : "text-muted-foreground")}>{formatDate(t.due, "short")}</span>}
                </button>
              </li>
            ))}
            {linked.length > 8 && <li className="px-1.5 text-caption text-muted-foreground">외 {linked.length - 8}개</li>}
          </ul>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {canCreate && (
            <Button size="sm" variant="outline" className="h-7 text-body font-normal" onClick={() => s().addExecutionTask({ linkedTaskIds: [scheduleTaskId], start, due: finish })}>
              <Add16Regular className="size-3.5" /> 연결된 Task 만들기
            </Button>
          )}
          {linked.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-body font-normal"
              onClick={() => (onViewTasks ? onViewTasks(scheduleTaskId) : setDialogOpen(true))}
            >
              작업 보기
            </Button>
          )}
        </div>
      </DetailSection>
      <LinkedTasksDialog open={dialogOpen} onClose={() => setDialogOpen(false)} scheduleTaskId={scheduleTaskId} />
    </>
  );
}

function DriverNote({
  driver,
  nameOf,
  projectStart,
  autoConstraint,
}: {
  driver: ScheduleDriver;
  nameOf: (id: string) => string;
  projectStart: string;
  autoConstraint: boolean;
}) {
  let text: string;
  switch (driver.kind) {
    case "project-start":
      text = `앞 작업이 없어 플랜 시작일(${formatDate(projectStart, "full")})에 시작합니다.`;
      break;
    case "constraint":
      text =
        driver.type === "MSO"
          ? `시작일이 ${formatDate(driver.date, "full")}로 고정되어 있습니다.`
          : autoConstraint
            ? `추가한 자리의 작업에 맞춰 ${formatDate(driver.date, "full")}에 시작합니다. 앞 작업을 연결하면 그 작업을 따릅니다.`
            : `"${formatDate(driver.date, "full")} 이후 시작" 조건이 앞 작업보다 늦어서 이 날 시작합니다.`;
      break;
    case "predecessor": {
      const lag = driver.lag ? (driver.lag > 0 ? ` +${driver.lag}d` : ` ${driver.lag}d`) : "";
      const rel = DEP_REL_KOREAN[driver.type];
      text = `앞 작업 '${nameOf(driver.predecessorId)}'(${rel}, ${driver.type}${lag}) 때문에 이 날 시작합니다.`;
      break;
    }
    case "children":
      text = "하위 작업 중 가장 이른 시작과 가장 늦은 종료를 그대로 씁니다.";
      break;
  }
  return <p className="mb-2.5 rounded-md border border-border/70 bg-muted/40 p-2.5 text-body leading-relaxed text-foreground/90">{text}</p>;
}

function DepRow({ dep, otherId, nameOf, editable }: { dep: Dependency; otherId: string; nameOf: (id: string) => string; editable: boolean }) {
  const s = useProjectStore.getState;
  return (
    <li className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1.5">
      <button type="button" className="min-w-0 flex-1 truncate text-left text-body hover:text-tint" onClick={() => s().select([otherId], otherId)} title={nameOf(otherId)}>
        {nameOf(otherId)}
      </button>
      {editable ? (
        <>
          <FuiDropdown
            value={dep.type}
            options={DEP_TYPE_OPTIONS}
            onValueChange={(v) => s().updateDependency(dep.id, { type: v as DependencyType })}
            style={{ width: 64, minWidth: 64 }}
          />
          <FuiInput
            type="number"
            value={String(dep.lag)}
            onChange={(_, d) => s().updateDependency(dep.id, { lag: Number(d.value) || 0 })}
            style={{ width: 56, minWidth: 56, textAlign: "right" }}
            title="앞 작업이 끝난 뒤 며칠 기다릴지 (음수면 그만큼 먼저 시작)"
          />
          <Button variant="ghost" size="icon-sm" className="size-7" onClick={() => s().removeDependency(dep.id)} aria-label="관계 삭제">
            <Dismiss16Regular className="size-4" />
          </Button>
        </>
      ) : (
        <span className="shrink-0 text-body text-muted-foreground tabular">
          {dep.type}
          {dep.lag ? ` ${dep.lag > 0 ? "+" : ""}${dep.lag}d` : ""}
        </span>
      )}
    </li>
  );
}

function ProgressMethodGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2.5 border-t border-border/70 pt-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded-sm py-1 text-left text-caption text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
      >
        <ChevronDown16Regular className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")} />
        측정 방법(퍼센트·0/100·50/50·마일스톤) 설명
      </button>
      {open && (
        <div className="mt-2 space-y-2.5 rounded-md border border-border/70 bg-muted/30 px-2.5 py-2">
          <dl className="space-y-2">
            {PROGRESS_METHOD_GUIDE.map((g) => (
              <div key={g.method}>
                <dt className="text-body font-normal text-foreground">{g.title}</dt>
                <dd className="mt-0.5 text-caption leading-relaxed text-muted-foreground">{g.detail}</dd>
              </div>
            ))}
          </dl>
          <p className="border-t border-border/60 pt-2 text-caption leading-relaxed text-muted-foreground">
            일정과 상위 작업 롤업에는 입력값이 아니라 이 방법을 적용한 진척을 씁니다.
          </p>
        </div>
      )}
    </div>
  );
}

function DependencyTypeGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2.5 border-t border-border/70 pt-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded-sm py-1 text-left text-caption text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
      >
        <ChevronDown16Regular className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")} />
        선행 관계 유형(FS·SS·FF·SF)과 lag 설명
      </button>
      {open && (
        <div className="mt-2 space-y-2.5 rounded-md border border-border/70 bg-muted/30 px-2.5 py-2">
          <dl className="space-y-2">
            {DEP_TYPE_GUIDE.map((g) => (
              <div key={g.type}>
                <dt className="text-body font-normal text-foreground">
                  {g.type} · {g.korean} ({g.english})
                </dt>
                <dd className="mt-0.5 text-caption leading-relaxed text-muted-foreground">{g.detail}</dd>
              </div>
            ))}
          </dl>
          <p className="border-t border-border/60 pt-2 text-caption leading-relaxed text-muted-foreground">
            <span className="text-foreground/85">lag</span>는 관계 기준일에서 며칠 기다릴지입니다. 양수면 그만큼 늦추고, 음수면 그만큼 당깁니다. (근무일)
          </p>
        </div>
      )}
    </div>
  );
}

function AddPredecessor({ taskId }: { taskId: string }) {
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const [value, setValue] = useState("");
  const options = project.tasks
    .filter((t) => t.id !== taskId)
    .map((t) => ({
      value: t.id,
      label: `${schedule.tasks.get(t.id)?.wbs ?? ""} ${t.name || "이름 없음"}`.trim(),
    }));
  return (
    <div className="mt-1.5">
      <FuiDropdown
        value={value}
        options={options}
        placeholder="선행 작업 추가 (FS)…"
        onValueChange={(v) => {
          setValue("");
          useProjectStore.getState().addDependency({ predecessorId: v, successorId: taskId, type: "FS", lag: 0 });
        }}
      />
    </div>
  );
}

function ReadValue({ value }: { value: string }) {
  return <div className="text-body font-normal text-foreground">{value || "—"}</div>;
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-2.5">
      <div className="mb-1 flex items-baseline justify-between text-body">
        <span className="font-normal text-foreground/85">{label}</span>
        {hint && <span className="text-caption text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export function KV({ label, value, className, hint }: { label: string; value: React.ReactNode; className?: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-body">
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        {label}
        {hint && (
          <Tooltip content={hint} relationship="description" withArrow positioning="after">
            <button
              type="button"
              aria-label={`${label} 설명`}
              className="inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground/70 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
            >
              <QuestionCircle12Regular />
            </button>
          </Tooltip>
        )}
      </span>
      <span className={cn("text-right font-normal text-foreground tabular", className)}>{value}</span>
    </div>
  );
}

function DebouncedInput({
  value,
  onCommit,
  type = "text",
  placeholder,
  hint,
  step,
}: {
  value: string;
  onCommit: (v: string) => void;
  type?: "text" | "number" | "email" | "password" | "search" | "tel" | "url" | "date";
  placeholder?: string;
  hint?: string;
  step?: string;
}) {
  const [v, setV] = useState(value);
  const latest = useRef(value);
  useEffect(() => {
    setV(value);
    latest.current = value;
  }, [value]);
  return (
    <div>
      <FuiInput
        type={type}
        step={step}
        value={v}
        placeholder={placeholder}
        onChange={(_, d) => {
          latest.current = d.value;
          setV(d.value);
        }}
        onBlur={() => onCommit(latest.current)}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            latest.current = value;
            setV(value);
          }
        }}
      />
      {hint && <p className="mt-1 text-caption text-muted-foreground">{hint}</p>}
    </div>
  );
}

function DebouncedTextarea({ value, onCommit, placeholder }: { value: string; onCommit: (v: string) => void; placeholder?: string }) {
  const [v, setV] = useState(value);
  const latest = useRef(value);
  useEffect(() => {
    setV(value);
    latest.current = value;
  }, [value]);
  return (
    <Textarea
      value={v}
      placeholder={placeholder}
      onChange={(e) => {
        latest.current = e.target.value;
        setV(e.target.value);
      }}
      onBlur={() => onCommit(latest.current)}
      className="min-h-14 bg-card text-body"
    />
  );
}
